import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HOLD_MS = 1300;
// A loose hand-drawn signature, drawn as you hold.
const SIGNATURE = "M30 14 C 26 30, 22 44, 16 50 C 11 55, 5 51, 9 45 C 14 38, 30 36, 44 38 M50 36 C 53 27, 59 24, 60 31 C 61 40, 64 42, 69 30 C 73 20, 80 16, 81 26 C 82 36, 78 44, 86 40 C 92 37, 95 30, 99 33 C 103 36, 98 43, 106 41 C 118 38, 122 12, 129 16 C 135 20, 124 42, 133 42 C 141 42, 146 33, 152 34 C 162 36, 148 47, 166 44 C 200 40, 238 34, 290 18"

export default function Pact() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const progress = useMotionValue(0);
  const anim = useRef(null);
  const [signed, setSigned] = useState(false);
  const [hint, setHint] = useState(false);
  const fill = useTransform(progress, (v) => `${v * 100}%`);
  const inkOpacity = useTransform(progress, [0, 0.02], [0, 1]);

  useEffect(() => () => anim.current?.stop(), []);

  const begin = () => {
    if (signed) return;
    setHint(false);
    anim.current?.stop();
    const remaining = (1 - progress.get()) * HOLD_MS;
    anim.current = animate(progress, 1, {
      duration: remaining / 1000,
      ease: "linear",
      onComplete: () => {
        setSigned(true);
        window.setTimeout(() => navigate("/auth"), reduce ? 200 : 1100);
      },
    });
  };

  const release = () => {
    if (signed) return;
    anim.current?.stop();
    if (progress.get() > 0.02) setHint(true);
    anim.current = animate(progress, 0, { duration: 0.45, ease: [0.16, 1, 0.3, 1] });
  };

  const onKeyDown = (e) => {
    if ((e.key === " " || e.key === "Enter") && !e.repeat) {
      e.preventDefault();
      begin();
    }
  };
  const onKeyUp = (e) => {
    if (e.key === " " || e.key === "Enter") release();
  };

  return (
    <div className="pact relative mx-auto max-w-2xl rounded-lg border border-white/10 bg-[#0d0d0d] px-6 py-10 sm:px-12 sm:py-12 text-left">
      <p className="font-serif-editorial text-2xl sm:text-[1.9rem] leading-snug text-[#F5F5F0]">
        I’ll show up when it’s inconvenient. I’ll answer the nudge.
        <span className="text-[#A1A1AA]"> And I won’t quit before my partner does.</span>
      </p>

      <div className="mt-10 relative h-16" aria-hidden="true">
        <svg viewBox="0 0 300 60" className="absolute inset-x-0 bottom-3 h-14 w-[70%] sm:w-[55%] overflow-visible">
          <motion.path
            d={SIGNATURE}
            fill="none"
            stroke="#F59E0B"
            strokeOpacity="0.55"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            transform="translate(1.6 1.3)"
            style={{ pathLength: progress, opacity: inkOpacity }}
          />
          <motion.path
            d={SIGNATURE}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pathLength: progress, opacity: inkOpacity }}
          />
        </svg>
        <div className="absolute inset-x-0 bottom-0 h-px bg-white/15" />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-[#7c7c84]">
        <span>Signature</span>
        <span className="tabular-nums">Day 1</span>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
        <button
          type="button"
          data-testid="pact-hold-button"
          onPointerDown={begin}
          onPointerUp={release}
          onPointerLeave={release}
          onPointerCancel={release}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onContextMenu={(e) => e.preventDefault()}
          aria-label={signed ? "Signed. Taking you to sign up." : "Press and hold to sign the pact and create your account"}
          className={`pact-hold relative overflow-hidden select-none touch-none inline-flex items-center justify-center gap-2 rounded-md px-8 py-4 text-sm font-semibold tracking-wide ${signed ? "is-signed" : ""}`}
        >
          <motion.span className="pact-hold-fill" style={{ width: fill }} aria-hidden="true" />
          <span className="relative">
            {signed ? "Signed. Finding your partner…" : "Press and hold to sign"}
          </span>
        </button>
        <span className={`text-sm font-light transition-opacity duration-300 ${hint && !signed ? "opacity-100 text-[#A1A1AA]" : "opacity-0"}`} role="status">
          Keep holding. Commitment takes a second.
        </span>
      </div>

      <Link
        to="/auth"
        data-testid="final-cta-button"
        className="group mt-8 inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
      >
        Or skip the ceremony and find my partner
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
      </Link>
    </div>
  );
}
